import { ArrowUpIcon, StopIcon } from '@heroicons/react/outline';
import { useQueryClient } from '@tanstack/react-query';
import { Alert } from '@yoast/ui-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

import {
  getGetBrandsQueryKey,
  getGetCurrentUserQueryKey,
  useCreateBrand,
  useGetBrands,
} from '../../api/generated/insights';
import type { BrandId } from '../../api/generated/insights.schemas';
import SparklesGradientIcon from '../../assets/icon-sparkles-gradient.svg?react';
import PageTitle from '../../components/Layout/PageTitle';
import { useBrandAnalysisMutations } from '../../contexts';
import CollectedInfoPanel from './CollectedInfoPanel';
import SearchQueryTicker from './SearchQueryTicker';
import SourceFavicons from './SourceFavicons';
import TurnDebugBadge from './TurnDebugBadge';
import { buildBrandCreatePayload } from './brandPayload';
import type { Citation, Profile, SignedTranscript, StatusPhase, TurnCost } from './types';
import { fmtMs, type TimingBreakdown } from './turnTiming';
import { type DonePayload, useStreamingOnboardingChat } from './useStreamingOnboardingChat';

// The opening ask is deterministic, so the UI shows it without a model round-trip
// (and no web-search latency). The model is first called only when the user replies.
const DEFAULT_OPENING_MESSAGE =
  "Hi! To get started, please share your **brand name** and the URL of your brand's **website**.";

// Max length of a single user message; mirrors the backend `userMessage` cap.
const MAX_MESSAGE_LENGTH = 1000;

// Max pixel height the auto-growing composer expands to before it scrolls
// internally (roughly six lines). Keeps a long paste from pushing the transcript
// off-screen.
const COMPOSER_MAX_HEIGHT = 160;

// A rendered turn in the transcript. Only visible user/assistant messages — no
// hidden kickoff/correction messages (sidebar edits ride along as profileEdits).
type Turn = {
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  cost?: TurnCost;
  timing?: TimingBreakdown;
};

const isSafeHttpUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

const PHASE_LABELS: Record<StatusPhase, string> = {
  reasoning: 'Thinking…',
  web_search: 'Searching the web…',
  writing: 'Writing a reply…',
};

const AssistantAvatar = () => (
  <div
    aria-hidden="true"
    className="yst-flex yst-h-8 yst-w-8 yst-shrink-0 yst-items-center yst-justify-center yst-rounded-full yst-bg-gradient-to-br yst-from-[#A61E69] yst-to-[#6366F1]"
  >
    <SparklesGradientIcon className="yst-h-4 yst-w-4 [&_path]:yst-stroke-white" />
  </div>
);

const AvatarSpacer = () => (
  <div aria-hidden="true" className="yst-h-8 yst-w-8 yst-shrink-0" />
);

const AssistantBubble = ({ children }: { children: React.ReactNode }) => (
  <div className="yst-max-w-[92%] sm:yst-max-w-[85%] yst-min-w-0 yst-break-words yst-rounded-2xl yst-bg-slate-100 yst-px-4 yst-py-2.5 yst-text-sm yst-text-slate-900">
    {children}
  </div>
);

const UserBubble = ({ children }: { children: React.ReactNode }) => (
  <div className="yst-max-w-[92%] sm:yst-max-w-[80%] yst-min-w-0 yst-break-words yst-break-all yst-rounded-2xl yst-bg-primary-500 yst-px-4 yst-py-2.5 yst-text-sm yst-text-white">
    {children}
  </div>
);

const MARKDOWN_CLASSES =
  '[&_p]:yst-mb-2 [&_p:last-child]:yst-mb-0 [&_ul]:yst-list-disc [&_ul]:yst-ml-5 [&_ul]:yst-mb-2 [&_ul:last-child]:yst-mb-0 [&_ol]:yst-list-decimal [&_ol]:yst-ml-5 [&_ol]:yst-mb-2 [&_ol:last-child]:yst-mb-0 [&_li]:yst-mb-0.5 [&_h1]:yst-text-base [&_h1]:yst-font-semibold [&_h1]:yst-mb-1 [&_h2]:yst-font-semibold [&_h2]:yst-mb-1 [&_h3]:yst-font-semibold [&_h3]:yst-mb-1 [&_code]:yst-bg-white [&_code]:yst-px-1 [&_code]:yst-rounded [&_pre]:yst-bg-white [&_pre]:yst-p-2 [&_pre]:yst-rounded [&_pre]:yst-overflow-x-auto [&_a]:yst-text-primary-600 [&_a]:yst-underline';

/** Live elapsed time since the bubble mounted (i.e. since the turn started). */
const useElapsedMs = (): number => {
  const t0 = useRef(performance.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed(performance.now() - t0.current), 200);
    return () => clearInterval(id);
  }, []);
  return elapsed;
};

const StreamingStatusBubble = ({
  phase,
  partialContent,
  queries,
  sources,
}: {
  phase: StatusPhase;
  partialContent: string;
  queries: string[];
  sources: Citation[];
}) => {
  const elapsed = useElapsedMs();

  return (
    <div role="status" aria-live="polite" className="yst-flex yst-w-full yst-items-start yst-gap-2">
      <AssistantAvatar />
      <div className="yst-max-w-[92%] sm:yst-max-w-[85%] yst-rounded-2xl yst-bg-slate-100 yst-px-4 yst-py-2.5 yst-text-sm yst-text-slate-700">
        <div className="yst-text-xs yst-text-slate-500">
          {PHASE_LABELS[phase]}
          <span className="yst-ml-1 yst-tabular-nums yst-text-slate-400">· {fmtMs(elapsed)}</span>
        </div>
        {phase === 'web_search' && <SearchQueryTicker queries={queries} />}
        {/* Favicons of the sites consulted so far — pop in as each search
            call finishes, and stay visible through the writing phase. */}
        <SourceFavicons citations={sources} />
        {partialContent.length > 0 && (
          <div className={`yst-mt-1 ${MARKDOWN_CLASSES}`}>
            <ReactMarkdown>{partialContent}</ReactMarkdown>
            <span className="yst-ml-0.5 yst-inline-block yst-h-3 yst-w-1.5 yst-animate-pulse yst-bg-slate-500 yst-align-middle" />
          </div>
        )}
      </div>
    </div>
  );
};

const OnboardingChat = () => {
  // Render-only transcript. Opens with the hardcoded greeting (no model call).
  const [turns, setTurns] = useState<Turn[]>(() => [
    { role: 'assistant', content: DEFAULT_OPENING_MESSAGE },
  ]);
  // Opaque, server-signed conversation state. Null before the first turn.
  const [signedTranscript, setSignedTranscript] = useState<SignedTranscript | null>(null);
  // The authoritative profile echoed by the server every turn (drives the sidebar).
  const [profile, setProfile] = useState<Profile | null>(null);
  // Sidebar edits made since the last send; ride along with the next message.
  const [pendingEdits, setPendingEdits] = useState<Partial<Profile>>({});
  const [totalCost, setTotalCost] = useState(0);

  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // The user message of the in-flight turn. If the turn errors or is stopped,
  // its bubble is removed (the model never saw it — the transcript is only
  // re-signed on success) and the text is restored into the composer so a
  // retry actually reaches the model.
  const pendingUserMessageRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Synchronous re-entry lock for the composer. `isPending` is React state and
  // only reflects the in-flight turn a render later, so a fast double-click on
  // Send fires two handlers in the same frame with `isPending` still false —
  // the second stream aborts the first, sending nothing. This ref flips
  // synchronously, so the second call bails out.
  const isSendingRef = useRef(false);
  const transcriptRef = useRef<HTMLDivElement>(null);
  // Stick-to-bottom: only autoscroll when the user is already near the bottom.
  const isAtBottomRef = useRef(true);

  const { state: streamState, send: streamSend, stop } = useStreamingOnboardingChat();
  const isPending = streamState.isStreaming;

  // --- Brand creation + navigation to the query-generation page ---
  // Mirrors Setup/1-BrandInfo.tsx: create the brand, set it active, then
  // navigate to /setup/:brandId/queries once it appears in the brands list.
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutations = useBrandAnalysisMutations();
  const [createdBrandId, setCreatedBrandId] = useState<BrandId | undefined>();
  const [generateError, setGenerateError] = useState<string | null>(null);

  const { mutate: postBrandCreate, isPending: isCreatingBrand } = useCreateBrand({
    mutation: {
      onSuccess: async (data) => {
        const brandId = data.data.id;
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getGetBrandsQueryKey() }),
        ]);
        if (data?.status && data.status >= 200 && data.status < 300 && brandId) {
          mutations.setBrandId(brandId);
          setCreatedBrandId(brandId);
        }
      },
      onError: () => {
        setGenerateError('Something went wrong while creating your brand. Please try again.');
      },
    },
  });

  const { data: brandsList } = useGetBrands({
    query: {
      enabled: !!createdBrandId,
      select: ({ data }) => data?.brands,
      refetchInterval: (query) =>
        (query?.state?.data?.data?.brands || []).find((b) => b.id === createdBrandId)
          ? false
          : 2 * 1000,
    },
  });

  useEffect(() => {
    if (createdBrandId && brandsList?.find((b) => b.id === createdBrandId)) {
      navigate(`/setup/${createdBrandId}/queries`, { replace: true });
    }
  }, [createdBrandId, brandsList, navigate]);

  const isGenerating = isCreatingBrand || !!createdBrandId;

  const handleGenerate = () => {
    if (!profile || isGenerating) return;
    setGenerateError(null);
    // Fold any un-sent sidebar edits into the profile we generate from. The
    // signed transcript rides along so the server can record how many messages
    // this onboarding took (null for wizard-created brands with no chat).
    postBrandCreate({
      data: buildBrandCreatePayload({ ...profile, ...pendingEdits } as Profile, signedTranscript),
    });
  };

  const handleTranscriptScroll = () => {
    const el = transcriptRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
  };

  useEffect(() => {
    if (!isAtBottomRef.current) return;
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
  }, [turns, isPending, streamState.partialContent]);

  // Discard the failed/stopped turn's user bubble and offer its text back in
  // the composer (unless the user has already typed ahead).
  const rollbackPendingUserMessage = useCallback(() => {
    const failed = pendingUserMessageRef.current;
    if (failed === null) return;
    pendingUserMessageRef.current = null;
    setTurns((prev) =>
      prev.length > 0 && prev[prev.length - 1].role === 'user' ? prev.slice(0, -1) : prev,
    );
    setDraft((prev) => (prev.trim() ? prev : failed));
  }, []);

  const onDone = useCallback(
    (done: DonePayload) => {
      const { turn } = done;
      const content = turn.assistant_message ?? '';
      pendingUserMessageRef.current = null;
      setSignedTranscript(done.signedTranscript);
      setProfile(turn.profile);
      setPendingEdits({}); // absorbed server-side; the returned profile is authoritative
      if (done.cost) setTotalCost((c) => c + done.cost!.total_cost);

      if (content.trim().length === 0) {
        setError('The assistant returned an empty reply. Try again.');
        return;
      }
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content,
          citations: done.citations ?? [],
          cost: done.cost,
          timing: done.timing,
        },
      ]);
    },
    [],
  );

  const sendText = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isPending || isSendingRef.current) return;
    isSendingRef.current = true;
    pendingUserMessageRef.current = trimmed;
    setTurns((prev) => [...prev, { role: 'user', content: trimmed }]);
    setDraft('');
    setError(null);

    try {
      await streamSend(
        {
          signedTranscript,
          userMessage: trimmed,
          ...(Object.keys(pendingEdits).length > 0 ? { profileEdits: pendingEdits } : {}),
        },
        {
          onDone,
          onError: (message) => {
            rollbackPendingUserMessage();
            setError(message);
          },
        },
      );
    } finally {
      isSendingRef.current = false;
    }
  };

  const send = () => {
    // Allow typing/pasting past the limit, but never send an over-length
    // message — the user trims it themselves rather than having it silently
    // truncated (which is easy to miss, especially when pasting).
    const trimmed = draft.trim();
    if (trimmed.length > MAX_MESSAGE_LENGTH) return;

    return sendText(trimmed);
  };

  const handleStop = () => {
    stop();
    // The partial turn is discarded (CONCEPT §4): the profile and transcript
    // stay unchanged, and the unsent message returns to the composer.
    rollbackPendingUserMessage();
  };

  // A sidebar field edit updates the sidebar immediately and accumulates as a
  // pending edit; it is sent with the user's next message (no per-edit turn).
  const handleSubmitField = (patch: Partial<Profile>) => {
    setProfile((prev) => ({ ...(prev ?? ({} as Profile)), ...patch }) as Profile);
    setPendingEdits((prev) => ({ ...prev, ...patch }));
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      // The input stays editable while the assistant responds (type-ahead per
      // CONCEPT §4), but a message can't be silently sent mid-turn.
      if (isPending) return;
      void send();
    }
    // Shift+Enter is left to the textarea, which inserts a newline natively.
  };

  const isOverLimit = draft.trim().length > MAX_MESSAGE_LENGTH;

  // Auto-grow the composer with its content, up to a cap (then it scrolls). Keyed
  // on `draft` so it also collapses back to one line when the field is cleared
  // on send.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [draft]);

  return (
    <div className="yst-flex yst-h-[calc(100dvh-8rem)] yst-min-h-[400px] yst-flex-col yst-p-4 sm:yst-h-[calc(100dvh-12rem)] sm:yst-min-h-[500px]">
      <PageTitle title="Build your brand profile - AI Brand Insights" />

      {(error || generateError) && (
        <div aria-live="assertive" aria-atomic="true" className="yst-mb-4 yst-shrink-0">
          <Alert variant="error">{generateError || error}</Alert>
        </div>
      )}

      <div className="yst-grid yst-grid-cols-1 yst-min-h-0 yst-flex-1 yst-gap-4 lg:yst-grid-cols-[2fr,1fr]">
        {/* Chat card */}
        <section
          aria-label="Onboarding chat"
          className="yst-flex yst-min-h-0 yst-flex-col yst-rounded-xl yst-bg-white yst-shadow-sm"
        >
          <header className="yst-border-b yst-border-slate-100 yst-px-3 sm:yst-px-6 yst-pt-6 yst-pb-5">
            <div className="yst-flex yst-items-baseline yst-gap-2">
              <span className="yst-text-xl sm:yst-text-2xl yst-font-semibold yst-text-primary-500">1</span>
              <span className="yst-text-xl sm:yst-text-2xl yst-font-light yst-text-slate-400">/</span>
              <span className="yst-text-xl sm:yst-text-2xl yst-font-light yst-text-slate-400">3</span>
              <h1 className="yst-ml-2 yst-text-xl sm:yst-text-2xl yst-font-semibold yst-text-slate-900">
                Build your brand profile
              </h1>
            </div>
            <p className="yst-mt-2 yst-text-sm yst-text-slate-600">
              Share your brand basics and we&apos;ll generate test queries to measure
              how often your brand appears in AI assistant responses.
            </p>
          </header>

          <div
            ref={transcriptRef}
            tabIndex={0}
            role="log"
            aria-live="polite"
            aria-label="Chat transcript"
            onScroll={handleTranscriptScroll}
            className="yst-flex yst-min-h-0 yst-flex-1 yst-flex-col yst-gap-3 yst-overflow-y-auto yst-px-3 sm:yst-px-6 yst-py-4"
          >
            {turns.map((message, index) => {
              if (message.role === 'user') {
                return (
                  <div key={index} className="yst-flex yst-justify-end">
                    <UserBubble>{message.content}</UserBubble>
                  </div>
                );
              }
              const prev = turns[index - 1];
              const showAvatar = !prev || prev.role !== 'assistant';
              return (
                <div key={index}>
                  <div className="yst-flex yst-items-start yst-gap-2">
                    {showAvatar ? <AssistantAvatar /> : <AvatarSpacer />}
                    <AssistantBubble>
                      <div className={MARKDOWN_CLASSES}>
                        <ReactMarkdown
                          components={{
                            a: ({ children, href }) =>
                              isSafeHttpUrl(href) ? (
                                <a href={href} target="_blank" rel="noopener noreferrer">
                                  {children}
                                </a>
                              ) : (
                                <span>{children}</span>
                              ),
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                      <SourceFavicons citations={message.citations ?? []} />
                    </AssistantBubble>
                  </div>
                  <TurnDebugBadge timing={message.timing} cost={message.cost} totalCost={totalCost} />
                </div>
              );
            })}
            {isPending && (
              <StreamingStatusBubble
                phase={streamState.phase}
                partialContent={streamState.partialContent}
                queries={streamState.queries}
                sources={streamState.sources}
              />
            )}
          </div>

          <div className="yst-shrink-0 yst-rounded-b-xl yst-border-t yst-border-slate-200 yst-bg-slate-50 yst-px-3 sm:yst-px-6 yst-py-4 yst-pb-[max(1rem,env(safe-area-inset-bottom))]">
            <label htmlFor="onboarding-chat-input" className="yst-sr-only">
              Your message
            </label>
            <div className="yst-flex yst-items-end yst-gap-2">
              <textarea
                id="onboarding-chat-input"
                ref={inputRef}
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="e.g. Yoast — https://yoast.com"
                className="yst-min-h-10 yst-flex-1 yst-resize-none yst-overflow-y-auto yst-rounded-md yst-border yst-border-slate-300 yst-bg-white yst-px-4 yst-py-2 yst-text-sm yst-text-slate-900 placeholder:yst-text-slate-400 focus:yst-border-primary-500 focus:yst-outline-none focus:yst-ring-1 focus:yst-ring-primary-500"
              />
              {isPending ? (
                <button
                  type="button"
                  onClick={handleStop}
                  aria-label="Stop generating"
                  className="yst-flex yst-h-10 yst-w-10 yst-shrink-0 yst-items-center yst-justify-center yst-rounded-md yst-border yst-border-slate-300 yst-bg-white yst-text-slate-600 hover:yst-bg-slate-100"
                >
                  <StopIcon className="yst-h-5 yst-w-5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={!draft.trim() || isOverLimit}
                  aria-label="Send message"
                  className="yst-flex yst-h-10 yst-w-10 yst-shrink-0 yst-items-center yst-justify-center yst-rounded-md yst-bg-primary-500 yst-text-white hover:yst-bg-primary-600 disabled:yst-cursor-not-allowed disabled:yst-bg-slate-300"
                >
                  <ArrowUpIcon className="yst-h-5 yst-w-5" />
                </button>
              )}
            </div>
            {draft.trim().length >= MAX_MESSAGE_LENGTH - 100 && (
              <div
                className={`yst-mt-1 yst-text-right yst-text-[10px] ${
                  isOverLimit ? 'yst-text-red-600' : 'yst-text-slate-400'
                }`}
              >
                {/* Live region announces the over-limit state once it appears.
                    The changing char count is kept outside it so screen readers
                    aren't spammed on every keystroke. */}
                <span aria-live="polite" className="yst-mr-1">
                  {isOverLimit ? 'Message is too long to send — please shorten it.' : ''}
                </span>
                {draft.trim().length}/{MAX_MESSAGE_LENGTH}
              </div>
            )}
          </div>
        </section>

        {/* Brand profile panel */}
        <CollectedInfoPanel
          extracted={profile}
          isStreaming={isPending}
          isGenerating={isGenerating}
          onSubmitField={handleSubmitField}
          onGenerate={handleGenerate}
        />
      </div>
    </div>
  );
};

export default OnboardingChat;
